import {
  ChevronDownGray600Icon,
  ChevronRightGray400Icon,
  ChevronRightGray600Icon,
  FolderGray400Icon,
  FolderPrimaryIcon
} from '@assets/iconComponents';
import {
  Frame,
  LoadingSpinner,
  MenuTop,
  SearchInput,
  Text,
  ThreeDImageListItemComponent,
  ThreeDImageModal
} from '@components';
import { styleConstant } from '@constant';
import { useThreeD } from '@hooks';
import { Button, styled } from '@mui/material';
import { colorSet } from '@theme';
import { IThreeDCategoryListItem, IThreeDImageListItem } from '@types';
import { useRef } from 'react';

// 스크롤바 커스터마이징을 위해 MUI styled component 이용
const CategoryContainer = styled('div')({
  width: '100%',
  height: 'calc(100% - 112px)', // 부모 컨테이너 - 페이지 타이틀 및 검색 인풋 & 그들의 여백
  overflowY: 'auto',
  paddingRight: '8px',

  '&::-webkit-scrollbar': {
    width: '8px',
    height: '0px'
  },

  '&::-webkit-scrollbar-thumb': {
    borderRadius: '8px',
    backgroundColor: colorSet.primaryMain
  },

  '&::-webkit-scrollbar-track': {
    borderRadius: '8px',
    backgroundColor: colorSet.gray200
  }
});

const ThreeDOutputPage = () => {
  const scrollToTop = () => {
    if (rightContainerRef.current) {
      rightContainerRef.current.scrollTop = 0;
    }
  };

  const {
    inputKeyword,
    searchKeyword,
    autocompleteList,
    handleKeyDown,
    handleInputKeywordChange,
    handleAutocompleteChange,
    handleClear,
    handleSubmit,

    expandedCategoryIds,
    selectedCategoryId,
    handleCategoryExpand,
    handleCategoryClick,
    categoryListData,

    selectedCategoryPath,
    imageListData,
    isLoading,
    flattenImageList,
    outputBottomRef,

    selectedAssetThreeDId,
    setSelectedAssetThreeDId
  } = useThreeD(scrollToTop);

  const rightContainerRef = useRef<HTMLDivElement>(null);
  const gap = 10;
  const itemNumberPerRow = 7;
  const sumOfHorizontalGaps = gap * (itemNumberPerRow - 1);
  const itemSize = `calc((100% - ${sumOfHorizontalGaps}px) / ${itemNumberPerRow})`;

  const renderCategoryList = (item: IThreeDCategoryListItem, level: number) => {
    const isExpanded = expandedCategoryIds.has(item.id);
    const isSelected = selectedCategoryId === item.id;
    const hasChildren = item.children && item.children.length > 0;
    const indent = level * 20;

    return (
      <div key={item.id}>
        {/* 현재 아이템 */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-start',
            gap: '4px',
            marginLeft: `${indent}px`
          }}
        >
          {/* Chevron 아이콘 */}
          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-start',
              alignItems: 'center',
              width: 20,
              height: level === 0 ? 40 : 32
            }}
          >
            {hasChildren && (
              <Button
                onClick={() => {
                  handleCategoryExpand(item.id);
                }}
                sx={{ width: '16px', minWidth: '16px', height: '16px', padding: 0 }}
              >
                {isExpanded ? <ChevronDownGray600Icon /> : <ChevronRightGray600Icon />}
              </Button>
            )}
          </div>

          <div
            onClick={() => handleCategoryClick(item.id)}
            style={{
              width: '100%',
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'flex-start',
              gap: '4px',
              padding: level === 0 ? '10px 0px' : '6px 0px',
              backgroundColor: isSelected ? 'rgba(119, 78, 255, 0.15)' : 'transparent',
              cursor: 'pointer'
            }}
          >
            {/* 폴더 아이콘 */}
            {isSelected ? <FolderPrimaryIcon /> : <FolderGray400Icon />}

            {/* 폴더명 */}
            {searchKeyword && item.nameKo.includes(searchKeyword) ? (
              <Text
                variant="body1"
                fontWeight={level === 0 ? 'bold' : 'normal'}
                color={isSelected ? colorSet.primaryMain : undefined}
                style={{ width: 'calc(100% - 24px)' }}
              >
                {item.nameKo.split('').map((char, index) => {
                  // searchKeyword의 모든 매칭 위치 찾기
                  const keywordLength = searchKeyword.length;
                  let isHighlighted = false;

                  // 현재 인덱스가 searchKeyword의 일부인지 확인
                  for (let i = 0; i <= item.nameKo.length - keywordLength; i++) {
                    if (item.nameKo.substring(i, i + keywordLength) === searchKeyword) {
                      if (index >= i && index < i + keywordLength) {
                        isHighlighted = true;
                        break;
                      }
                    }
                  }

                  return (
                    <span
                      key={index}
                      style={{
                        color: isHighlighted ? colorSet.primaryMain : colorSet.gray600
                      }}
                    >
                      {char}
                    </span>
                  );
                })}
              </Text>
            ) : (
              <Text
                variant="body1"
                fontWeight={level === 0 ? 'bold' : 'normal'}
                color={
                  isSelected || (searchKeyword && searchKeyword === item.nameKo) ? colorSet.primaryMain : undefined
                }
                style={{ width: 'calc(100% - 24px)' }}
              >
                {item.nameKo}
              </Text>
            )}
          </div>
        </div>

        {/* 자식 아이템 렌더링 */}
        {isExpanded && hasChildren && item.children.map((child: any) => renderCategoryList(child, level + 1))}
      </div>
    );
  };

  return (
    <>
      <MenuTop />

      <div
        style={{
          height: `calc(100vh - ${styleConstant.menuTopHeight}px)`,
          display: 'flex',
          flexDirection: 'row'
        }}
      >
        {/* 좌측 메뉴 */}
        <div
          style={{
            width: '352px',
            height: '100%',
            padding: '4px 16px'
          }}
        >
          <div
            style={{
              height: '100%',
              padding: '20px 0px'
            }}
          >
            <Text variant="h2" fontWeight="bold" marginBottom="24px">
              3D 소재
            </Text>

            <SearchInput
              placeholder="검색"
              value={inputKeyword}
              options={autocompleteList}
              onKeyDown={handleKeyDown}
              onInputChange={handleInputKeywordChange}
              onAutocompleteChange={handleAutocompleteChange}
              onClear={handleClear}
              onSubmit={handleSubmit}
              showTwoSubIcons={true}
              boxstyle={{ marginBottom: '12px' }}
            />

            {/* 카테고리 리스트 */}
            <CategoryContainer>
              {categoryListData && categoryListData.length > 0 ? (
                categoryListData.map((item) => renderCategoryList(item, 0))
              ) : (
                <>
                  {searchKeyword && searchKeyword.length > 0 ? (
                    <div
                      style={{
                        width: '100%',
                        height: '100px',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center'
                      }}
                    >
                      <Text variant="body1" color={colorSet.gray500}>
                        검색된 카테고리가 없습니다.
                      </Text>
                    </div>
                  ) : null}
                </>
              )}
            </CategoryContainer>
          </div>
        </div>

        <div style={{ height: '100%', borderRight: `1px solid ${colorSet.gray300}` }} />

        {/* 우측 이미지 리스트 */}
        <div
          ref={rightContainerRef}
          className="hide-scrollbar"
          style={{
            flex: 1,
            height: '100%',
            backgroundColor: colorSet.white,
            overflowY: 'auto'
          }}
        >
          <Frame width="100%" padding="24px 40px 96px 40px">
            {selectedCategoryPath && selectedCategoryPath.length > 0 ? (
              <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', marginBottom: '20px' }}>
                {selectedCategoryPath.map((item, index) => {
                  return (
                    <div key={index} style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                      <Text variant="body1" color={colorSet.gray500} marginRight="10px">
                        {item}
                      </Text>
                      {selectedCategoryPath.length - 1 !== index && (
                        <ChevronRightGray400Icon style={{ marginRight: '10px' }} />
                      )}
                    </div>
                  );
                })}
              </div>
            ) : null}

            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: `${gap}px`,
                width: '100%',
                boxSizing: 'border-box'
              }}
            >
              {imageListData &&
                flattenImageList.length > 0 &&
                flattenImageList.map((item: IThreeDImageListItem, index) => {
                  return (
                    <ThreeDImageListItemComponent
                      key={index}
                      onClick={() => {
                        setSelectedAssetThreeDId(item.id);
                      }}
                      item={item}
                      itemSize={itemSize}
                    />
                  );
                })}
            </div>
            <div ref={outputBottomRef} />

            {(!imageListData || flattenImageList.length === 0) && (
              <div
                style={{
                  height: '100px',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center'
                }}
              >
                {isLoading ? (
                  <LoadingSpinner />
                ) : (
                  <Text variant="body1" color={colorSet.gray500}>
                    검색된 이미지가 없습니다.
                  </Text>
                )}
              </div>
            )}
          </Frame>
        </div>
      </div>

      <ThreeDImageModal
        isVisible={Boolean(selectedAssetThreeDId)}
        onClose={() => {
          setSelectedAssetThreeDId(null);
        }}
        assetThreeDId={selectedAssetThreeDId}
      />
    </>
  );
};

export default ThreeDOutputPage;
